import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { classes } from "../../util/style-utils";
import PageHeader from '../PageHeader/PageHeader';
import Layout from "../Layout";
import React, { useEffect, useState, useRef, useContext } from "react";
import { useMask } from '@react-input/mask';
import { GdkModal } from "@gdk/modal";
import { addBankInfo, getBankName, getBankInfo } from "../../api/billing";
import { getAgencyProfile } from "../../api/agency-profile";
import { UserContext } from '../../context/UserContextProvider';
import { GdkToasts } from "@gdk/toasts";
import { useNavigate } from "react-router-dom";

const validationSchema = yup
    .object({
        accountType: yup.string().trim().required("Please select an account type."),
        bankName: yup.string().trim().required("Please enter the name of your bank."),
        routingNumber: yup.string().trim().matches(/^\d{9}$/, { message: "Please enter a valid 9 digit routing number.", excludeEmptyString: true })
            .required("Please enter a valid 9 digit routing number."),
        accountNumber: yup.string().trim().required("Please enter a valid account number."),
        verifyAccountNumber: yup.string().trim().required("Please ensure the verified account number matches the account number.")
            .oneOf([yup.ref("accountNumber")], "Please ensure the verified account number matches the account number."),
        authorize: yup.bool().oneOf([true], "You must authorize the update").required('You must authorize the update'),
    });

export default function ManageCommissionBankingDetails() {

    const [disableBankName, setDisableBankName] = useState(true);
    const [validBankInfo, setValidBankInfo] = useState();
    const [agency, setAgency] = useState({});
    const user = useContext(UserContext);
    const bankInfoFieldNotAvailable = "N/A"
    const [bankInfo, setBankInfo] = useState(false);
    const gdkSuccessToast = useRef(null);
    const gdkInformationToast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (user && user.agencyId) {
            getAgencyProfile(user.agencyId, (response) => {
                setAgency(response);
            })
        }
    }, [user]);

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors, isValid },
    } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange'
    });

    const accountTypes = [
        { title: "Checking", value: "Checking" },
        { title: "Savings", value: "Savings" },
    ];

    const getBankDetails = (routingNumber) => {

        const handleError = (error) => {
            setValue('bankName', null);
            setDisableBankName(false);
        };

        const handleSuccess = (responseData) => {
            if (responseData) {
                setValue('bankName', responseData.bankName);
                setDisableBankName(true);
            }
            else {
                setDisableBankName(false);
            }
        }

        if (routingNumber && routingNumber.length == 9) {
            getBankName(routingNumber, handleSuccess, handleError)
        }
    }

    const { ref: registerRoutingNumberRef, ...registerRoutingNumber } = register("routingNumber");
    const { ref: registerAccountNumberRef, ...registerAccountNumber } = register("accountNumber");
    const { ref: registerVerifyAccountNumberRef, ...registerVerifyAccountNumber } = register("verifyAccountNumber");

    const maskRoutingNumberRef = useMask({ mask: '_________', replacement: { _: /\d/ }, onMask: (event) => { routingNumberUnmasked.current = event.detail?.input; getBankDetails(routingNumberUnmasked.current); } });
    const maskAccountNumberRef = useMask({ mask: '______________________________', replacement: { _: /\d/ }, onMask: (event) => { accountNumberUnmasked.current = event.detail?.input; } });
    const maskVerifyAccountNumberRef = useMask({ mask: '______________________________', replacement: { _: /\d/ }, onMask: (event) => { verifyAccountNumberUnmasked.current = event.detail?.input; } });

    const routingNumberUnmasked = useRef("");
    const accountNumberUnmasked = useRef("");
    const verifyAccountNumberUnmasked = useRef("");

    const onSubmit = (values) => {

        const request = {
            ...values,
            validationRequired: 'YES',
            achBankAccountType: values.accountType,
            requestType: 'Create'
        };

        const handleError = (error) => {
            gdkInformationToast.current.show();
        };

        const handleSuccess = (response) => {
            reset();
            gdkSuccessToast.current.show();
        }

        addBankInfo(request, handleSuccess, handleError);
    };

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const gdkModal = useRef(null);
    const modalIdRef = useRef("cancel-modal");

    const onCancelConfirm = () => {
        setShowConfirmationModal(false);
        navigate("/bank-info");
    }

    useEffect(() => {
        gdkModal.current = new GdkModal({
            content: "#" + modalIdRef.current,
            hideCloseButton: true,
            onClosed: () => {
                setShowConfirmationModal(false);
            },
        });
    }, []);

    useEffect(() => {
        gdkSuccessToast.current = new GdkToasts({
            content: "#success-toast",
            floatTime: "short",
            closeBtn: true,
            baseTimer: true,
        });

        gdkInformationToast.current = new GdkToasts({
            content: "#information-toast",
            floatTime: "short",
            closeBtn: true,
            baseTimer: true,
        });
    }, []);

    useEffect(() => {
        if (showConfirmationModal === true) {
            gdkModal.current.show();
        }
        if (showConfirmationModal === false) {
            gdkModal.current.hide();
        }
    }, [showConfirmationModal]);

    useEffect(() => {
        setValidBankInfo(
            bankInfo?.achBankAccountType &&
            bankInfo?.paymentMethods &&
            bankInfo?.paymentMethods[0]?.institutionName !== undefined &&
            bankInfo?.bankAccountNumber &&
            bankInfo.bankAccountNumber !== undefined &&
            bankInfo?.bankRoutingNumber
        )
    }, [bankInfo]);

    useEffect(() => {
        getBankInfo(
            (response) => {
                setBankInfo(response);
            },
            () => { }
        );
    }, []);

    return (
        <Layout>
            <PageHeader
                header="Manage Commission Banking Details"
            />
            <div id="success-toast" className="toasts-container">
                <div className="toasts toasts--confirmation">
                    <div>
                        <span className="icon-confirmation" />
                    </div>
                    <div role="status">
                        <div className="toasts--heading">Success!</div>
                        <div className="toasts--content">Your commission banking information has been updated. Changes will take effect for the next commission cycle.</div>
                    </div>
                    <button className="toasts--close-btn icon-close">
                        <div id="toasts--base-timer" />
                    </button>
                </div>
            </div>
            <div id="information-toast" className="toasts-container">
                <div className="toasts toasts--update">
                    <div>
                        <span className="icon-info" />
                    </div>
                    <div role="status">
                        <div className="toasts--heading">Information</div>
                        <div className="toasts--content">We are unable to update your commission banking information. Please ensure that the account number entered is valid. If you continue to experience issues, contact our help desk at 1-800-624-2513 for further assistance.</div>
                    </div>
                    <button className="toasts--close-btn icon-close">
                        <div id="toasts--base-timer" />
                    </button>
                </div>
            </div>
            <div className="container margin-top">
                <div className="row">
                    <div className={`${validBankInfo ? "col-sm-6" : "col-sm-3"}`}>
                        {
                            validBankInfo &&
                            <>
                                <h3 className="mb-5">Current Payout Method on File</h3>
                                <div className="row">
                                    <div className="col-sm-4">
                                        <label className="text">
                                            Account Type :
                                        </label>
                                    </div>
                                    <div className="col-sm-8">
                                        <label>
                                            {bankInfo.achBankAccountType}
                                        </label>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-4">
                                        <label className="text">
                                            Bank Name :
                                        </label>
                                    </div>
                                    <div className="col-sm-8">
                                        <label>
                                            {bankInfo.paymentMethods[0]?.institutionName}
                                        </label>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-4">
                                        <label className="text">
                                            Account Number :
                                        </label>
                                    </div>
                                    <div className="col-sm-8">
                                        <label>
                                            {
                                                bankInfo.bankAccountNumber !== undefined &&
                                                `Ending in ${bankInfo.bankAccountNumber?.slice(-4)}`
                                            }
                                        </label>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-4">
                                        <label className="text">
                                            Routing Number :
                                        </label>
                                    </div>
                                    <div className="col-sm-8">
                                        <label>
                                            {bankInfo.bankRoutingNumber}
                                        </label>
                                    </div>
                                </div>
                            </>
                        }

                    </div>

                    <div className="col-sm-6">
                        <h3 className="mb-5">Update Banking Information</h3>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                <div className="col-sm-12">
                                    <fieldset
                                        role="radiogroup"
                                        className={`form-field radio-button-group ${errors.accountType && `form-field--error`
                                            }`}
                                    >
                                        <div className="radio-button-wrapper col-2">
                                            {accountTypes?.map((option) => (
                                                <div
                                                    key={`${option.value == "Savings" ? "Savings" : "Checking"}-radio`}
                                                >
                                                    <input
                                                        id={`${option.value == "Savings" ? "Savings" : "Checking"
                                                            }-radio`}
                                                        type="radio"
                                                        value={option.value}
                                                        {...register("accountType")}
                                                    />
                                                    <label
                                                        className="radio"
                                                        htmlFor={`${option.value == "Savings" ? "Savings" : "Checking"
                                                            }-radio`}
                                                    >
                                                        <span>{option.title}</span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </fieldset>
                                </div>
                            </div>
                            <div className="row">
                                <div
                                    style={{ width: '100%' }}
                                    className={classes(
                                        "form-field col-sm-6",
                                        errors.routingNumber && "form-field--error"
                                    )}
                                >
                                    <label htmlFor="routingNumber" className="text">
                                        Routing Number
                                    </label>
                                    <input
                                        id="routingNumber"
                                        type="text"
                                        style={{ width: '100%' }}
                                        placeholder="Routing Number"
                                        ref={(e) => {
                                            registerRoutingNumberRef(e);
                                            maskRoutingNumberRef.current = e;
                                        }}
                                        {...registerRoutingNumber}
                                    />
                                    {errors.routingNumber && (
                                        <span className="form-message">
                                            {errors.routingNumber.message}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                <div
                                    style={{ width: '100%' }}
                                    className={classes(
                                        "form-field col-sm-6",
                                        errors.bankName && "form-field--error"
                                    )}
                                >
                                    <label htmlFor="bankName" className="text">
                                        Bank Name
                                    </label>
                                    <input
                                        id="bankName"
                                        type="text"
                                        style={{ width: '100%' }}
                                        placeholder="Bank Name"
                                        {...register("bankName")}
                                        disabled={disableBankName}
                                    />
                                    {errors.bankName && (
                                        <span className="form-message">
                                            {errors.bankName.message}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="row">
                                <div
                                    style={{ width: '100%' }}
                                    className={classes(
                                        "form-field col-sm-6",
                                        errors.accountNumber && "form-field--error"
                                    )}
                                >
                                    <label htmlFor="accountNumber" className="text">
                                        Account Number
                                    </label>
                                    <input
                                        id="accountNumber"
                                        type="text"
                                        style={{ width: '100%' }}
                                        placeholder="Account Number"
                                        ref={(e) => {
                                            registerAccountNumberRef(e);
                                            maskAccountNumberRef.current = e;
                                        }}
                                        {...registerAccountNumber}
                                    />
                                    {errors.accountNumber && (
                                        <span className="form-message">
                                            {errors.accountNumber.message}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                <div
                                    style={{ width: '100%' }}
                                    className={classes(
                                        "form-field col-sm-6",
                                        errors.verifyAccountNumber && "form-field--error"
                                    )}
                                >
                                    <label htmlFor="verifyAccountNumber" className="text">
                                        Verify Account Number
                                    </label>
                                    <input
                                        id="verifyAccountNumber"
                                        type="text"
                                        style={{ width: '100%' }}
                                        placeholder="Verify Account Number"
                                        ref={(e) => {
                                            registerVerifyAccountNumberRef(e);
                                            maskVerifyAccountNumberRef.current = e;
                                        }}
                                        {...registerVerifyAccountNumber}
                                    />
                                    {errors.verifyAccountNumber && (
                                        <span className="form-message">
                                            {errors.verifyAccountNumber.message}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <fieldset
                                    role="radiogroup"
                                    className={`form-field radio-button-group ${errors.authorize && `form-field--error`
                                        }`}
                                >
                                    <div className="checkbox-wrapper">
                                        <div>
                                            <input
                                                id="authorize"
                                                type="checkbox"
                                                {...register("authorize")}
                                            />
                                            <label htmlFor="authorize" className="checkbox">
                                                I authorize GEICO to deposit my commission payments into the above account.
                                            </label>
                                            {errors.authorize && (
                                                <span className="form-message"> {errors.authorize?.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                            <div className="row">
                                <div className="col-sm-12">
                                    <button
                                        type="submit"
                                        className="btn btn--primary btn--full-mobile btn--pull-right"
                                        disabled={!isValid}
                                    >
                                        <span>Save</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                                        onClick={() => setShowConfirmationModal(true)}
                                    >
                                        <span>Cancel</span>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div id="cancel-modal" className="modal" >
                <div className="modal-container" style={{ height: '25rem', width: '30%' }}>
                    <h4 className="modal-headline"> Are you sure you want to cancel? </h4>
                    <div className="modal-content modal-content-with-cta-bar">
                        Your information will remain unchanged
                    </div>
                    <div className="modal-content">
                        <div className="modal--call-to-action-bar">
                            <button type="button" className="btn btn--secondary btn--full-mobile btn--pull-right" onClick={onCancelConfirm}>
                                <span>Yes</span>
                            </button>
                            <button type="button" className="btn btn--secondary btn--full-mobile btn--pull-right" onClick={() => setShowConfirmationModal(false)}>
                                <span>No</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
